import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, MessageCircle, MapPin, Clock, Navigation } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  loader: async (): Promise<{ content: PageContent }> => {
    const { CONTACT_CONTENT } = await import("@/content/site-pages");
    return { content: CONTACT_CONTENT };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `ติดต่อ Thai Notary ${config.nameEn} — โทร / LINE / Email / สาขา`;
    const description = `ติดต่อทีมงาน Thai Notary Law & Service ใน${config.nameTh} — โทร ${COMPANY.phone}, LINE ${COMPANY.lineId}, อีเมล ${COMPANY.email}, เปิดทุกวัน 08:00–20:00`;
    const head = pageHeadForProvince({ title, description, path: "/contact" });
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/contact")) },
        { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ติดต่อเรา", url: `${site}/contact` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": `${site}/contact#contactpage`,
            url: `${site}/contact`,
            name: title,
            description,
            mainEntity: { "@id": `${site}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(loaderData?.content.faqs ?? [])),
        },
      ],
    };
  },
});

function ContactPage() {
  const { content } = Route.useLoaderData();
  const { config } = getActiveProvince();
  const channels = [
    { icon: Phone, label: "โทรศัพท์", value: COMPANY.phone, href: `tel:${COMPANY.phoneIntl}`, hint: "ตอบรับทันที 08:00–20:00 ทุกวัน" },
    { icon: MessageCircle, label: "LINE Official", value: COMPANY.lineId, href: `https://line.me/R/ti/p/${COMPANY.lineId}`, hint: `ตอบกลับ ${COMPANY.responseTime}` },
    { icon: Mail, label: "อีเมล", value: COMPANY.email, href: `mailto:${COMPANY.email}`, hint: "เหมาะกับงานที่ต้องส่งไฟล์/สแกน" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center">
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              ติดต่อทีมงาน Thai Notary {config.nameEn}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
              ตอบกลับ {COMPANY.responseTime} ทุกช่องทาง — เปิดทำการ {COMPANY.hours}
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-4 px-4 py-12 md:grid-cols-3">
            {channels.map((c) => (
              <a
                key={c.label}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="block rounded-xl border border-border bg-card p-6 transition hover:border-primary hover:shadow-md"
              >
                <c.icon className="mb-3 h-7 w-7 text-primary" />
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</div>
                <div className="mt-1 break-all text-lg font-extrabold text-foreground">{c.value}</div>
                <p className="mt-2 text-xs text-muted-foreground">{c.hint}</p>
              </a>
            ))}
          </div>
        </section>

        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-14">
            <h2 className="mb-6 text-2xl font-bold md:text-3xl">สาขา & จุดบริการใน{config.nameTh}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {config.branches.map((b) => (
                <div key={b.id} className="rounded-xl border border-border bg-card p-5">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <h3 className="text-base font-bold">{b.name}</h3>
                    {b.isPrimary ? (
                      <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold">HQ</span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>{b.address}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> {COMPANY.hours}
                  </div>
                  <a
                    href={b.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    <Navigation className="h-3.5 w-3.5" /> เปิดแผนที่ Google
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <LongFormContent content={content} />

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-xl font-bold md:text-2xl">โทรเลย — ทีมงาน{config.nameTh}พร้อมรับสาย</h2>
            <a
              href={`tel:${COMPANY.phoneIntl}`}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 text-base font-bold text-gold-foreground"
            >
              <Phone className="h-5 w-5" /> {COMPANY.phone}
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}